import { Dispatch } from "redux";
import { MapDispatchToProps, MapStateToPropsFactory } from "react-redux";

import { BetslipAccordionHeaderClick, UI__BETSLIP_ACCORDION_HEADER_CLICK } from "@ppb/tbd-store/actions/betslip";
import { getSportsbookBettingLegs } from "@ppb/tbd-store/state/betting/sportsbook-betting/sportsbook-betting-selectors";
import { getBettingResolvers } from "../../../store/state/sportsbook-betting/betting-resolver";
import { getSportsbookBettingImplyRunnerFailures } from "../../../store/state/sportsbook-betting/sportsbook-betting-selectors";
import {
  getSportsbookConfirmationLegs,
  getBetslipGroup,
} from "../../../store/state/betslip-module/betslip-module-selectors";
import { BetslipApplicationState } from "../../../store/state/BetslipApplicationState";

import { i18n } from "../../../helpers/i18n";
import { createIsConfirmStep } from "../../sportsbook-betslip-confirm-mapper";
import { ContainerProps, DispatchProps, StateProps } from "../props";
import { buildHintGroups, LegHintContext } from "../../../helpers/hint-groups";
import { getBetBuilderLegHint } from "../../../helpers/leg-hint";
import { BettingState, RUNNER_FAILURE_CODES } from "@ppb/betslip-core";
import type { BettingMetadata } from "@ppb/tbd-store/state/betting/sportsbook-betting/SportsbookBetting.types";

export const makeMapStateToProps: MapStateToPropsFactory<StateProps, ContainerProps, BetslipApplicationState> = () => {
  const getIsConfirmStep = createIsConfirmStep();

  return (state, { legIds, isWarning, capabilities }) => {
    const isConfirmStep = getIsConfirmStep(state);

    if (!legIds) {
      return { title: "", legIds: [] };
    }

    const legs = isConfirmStep ? getSportsbookConfirmationLegs(state) : getSportsbookBettingLegs(state);
    const failures = getSportsbookBettingImplyRunnerFailures(state);

    const group = getBetslipGroup(state);
    const resolvers = getBettingResolvers(group);
    const runnersMetadata = resolvers.getMetadata(state);
    const hasSelectionConnector = capabilities.selectionConnector.enabled;

    const getBetBuilderHintScope = (
      metadata: BettingMetadata | undefined,
      runnerFailures: BettingState.ImplyRunnerFailure[],
    ): string | undefined => {
      const hasImpossibleSameMarketCombination = runnerFailures.some(
        ({ failureCode }) => failureCode === RUNNER_FAILURE_CODES.IMPOSSIBLE_SAME_MARKET_COMBINATION,
      );

      return hasImpossibleSameMarketCombination ? metadata?.marketType : undefined;
    };
    const buildLegHintContext = (legId: string): LegHintContext => {
      const runnerId = legs[legId]?.runners?.[0];
      const runnerFailures = (runnerId && failures[runnerId]) || [];
      const metadata = runnerId ? runnersMetadata[runnerId] : undefined;

      return {
        legId,
        hint: getBetBuilderLegHint(runnerFailures),
        scope: getBetBuilderHintScope(metadata, runnerFailures),
      };
    };

    const legHints = buildHintGroups(legIds.map(buildLegHintContext));

    return {
      title: isWarning
        ? i18n({ key: "I18N.BETSLIP.NOT_COMBINABLE" })
        : i18n({
            key: "I18N.BETSLIP.SELECTIONS_COUNT",
            interpolationValues: { numberOfSelections: `${legIds.length}` },
          }),
      legIds,
      legHints,
      hasSelectionConnector,
    };
  };
};

export const mapDispatchToProps: MapDispatchToProps<DispatchProps, ContainerProps> = (dispatch: Dispatch) => ({
  dispatchCollapseToggle: (isExpanded: boolean) => {
    dispatch<BetslipAccordionHeaderClick>({
      type: UI__BETSLIP_ACCORDION_HEADER_CLICK,
      payload: { isExpanded },
    });
  },
});
